import { array, boolean, discriminatedUnion, enum as enumFactory, iso, literal, number, strictObject, string } from "../../lazy-zod.js";
import { agentProfileIdSchema, profileWorkingDirectorySchema, profileWorkspaceModeSchema } from "../protocol/profile.js";
import { localAgentCatalogWithLifecycleSchema } from "../runtime/local-agent-catalog.js";
import { daemonServiceLaneSchema } from "../daemon/service-contract.js";
import { START_BOUNDED_ID_SCHEMA } from "../start/shared.js";
import { localComponentUpdateProjectionSchema, openMeldToolsMaintenanceMutationResultSchema, openMeldToolsUpdateRequestResultSchema } from "../local-components.js";
import { localAgentInstallationOptionSchema, openMeldDaemonSyncRouteCatalogPayloadSchema } from "../daemon/control-plane.js";
import { daemonExecutionCompatibilitySchema } from "../daemon/runtime-transport.js";
import { agentControllerInterfaceSnapshotSchema } from "../runtime/agent-controller-interface.js";
import { prepareReadinessRuntimeIdSchema } from "./prepare-readiness.js";
//#region src/schemas/http/local-runtimes.ts
const ISO_DATE_TIME_SCHEMA = iso.datetime({ offset: true });
const reasonCodeSchema = string().trim().min(1).max(256);
const localRuntimeDisplayNameSourceSchema = enumFactory([
	"user",
	"observed",
	"placeholder"
]);
const localRuntimePlatformFamilySchema = enumFactory([
	"macOS",
	"Windows",
	"Linux",
	"Unknown"
]);
const localRuntimeDeviceTypeSchema = enumFactory([
	"laptop",
	"desktop",
	"server",
	"unknown"
]);
const localRuntimeExecutionTargetCapabilitySchema = strictObject({
	agentControllerRef: string().trim().min(1).max(256),
	agentControllerInterface: agentControllerInterfaceSnapshotSchema.nullable().optional(),
	enabled: boolean(),
	reasonCodes: array(reasonCodeSchema),
	status: enumFactory([
		"ready",
		"not_ready",
		"stale",
		"unknown"
	])
}).superRefine((value, context) => {
	if (value.agentControllerInterface && value.agentControllerInterface.agentControllerRef !== value.agentControllerRef) context.addIssue({
		code: "custom",
		message: "agentControllerInterface.agentControllerRef must match capability agentControllerRef",
		path: ["agentControllerInterface", "agentControllerRef"]
	});
});
const localRuntimeLocalAgentControlSchema = strictObject({
	manageInstallations: boolean().optional(),
	manageInstallationsSafely: boolean().optional(),
	refreshCatalog: boolean(),
	setEnabled: boolean(),
	setProfileWorkspace: boolean().optional()
});
const localRuntimeLocalProjectControlSchema = strictObject({
	connectCandidate: boolean(),
	connectDeclaredPath: boolean(),
	discoverProjects: boolean(),
	moveConnection: boolean().optional(),
	removeConnection: boolean(),
	removeMissingConnections: boolean(),
	setDefaultConnection: boolean()
});
const localRuntimeComputerFileAccessSchema = strictObject({
	readPolicy: boolean(),
	updatePolicy: boolean()
});
const localRuntimeLocalFolderPickerSchema = strictObject({ selectFolders: boolean() });
const localRuntimeExecutionTargetStatusSchema = enumFactory([
	"ready",
	"partial",
	"not_ready",
	"stale",
	"unknown"
]);
const localRuntimeExecutionTargetSchema = strictObject({
	deviceId: START_BOUNDED_ID_SCHEMA,
	runtimeLane: daemonServiceLaneSchema,
	userFacingCommand: enumFactory([
		"openmeld",
		"openmeld-dev",
		"openmeld-canary"
	]),
	openMeldServiceRuntimeId: prepareReadinessRuntimeIdSchema.nullable(),
	status: localRuntimeExecutionTargetStatusSchema,
	capabilities: array(localRuntimeExecutionTargetCapabilitySchema),
	localAgentControl: localRuntimeLocalAgentControlSchema,
	localProjectControl: localRuntimeLocalProjectControlSchema.optional(),
	localAgentCatalog: localAgentCatalogWithLifecycleSchema,
	reasonCodes: array(reasonCodeSchema),
	lastObservedAtMs: number().int().nonnegative().nullable(),
	isCurrent: boolean()
});
const localRuntimeConnectionStatusSchema = enumFactory(["connected", "legacy_disconnected"]);
const localRuntimeComputerSchema = strictObject({
	connectedComputerId: START_BOUNDED_ID_SCHEMA.optional(),
	connectionStatus: localRuntimeConnectionStatusSchema.optional(),
	deviceId: START_BOUNDED_ID_SCHEMA,
	deviceDisplayName: string().min(1),
	deviceDisplayNameSource: localRuntimeDisplayNameSourceSchema,
	platformFamily: localRuntimePlatformFamilySchema,
	osVersion: string().trim().min(1).max(120).nullable(),
	deviceType: localRuntimeDeviceTypeSchema,
	firstSeenAtMs: number().int().nonnegative(),
	lastObservedAtMs: number().int().nonnegative().nullable(),
	isCurrent: boolean(),
	computerFileAccess: localRuntimeComputerFileAccessSchema.optional(),
	localFolderPicker: localRuntimeLocalFolderPickerSchema.optional(),
	legacyDeviceIds: array(START_BOUNDED_ID_SCHEMA).optional(),
	executionTargets: array(localRuntimeExecutionTargetSchema)
});
const localRuntimeLegacyDeviceEvidenceSchema = strictObject({
	connectionStatus: literal("legacy_disconnected"),
	deviceId: START_BOUNDED_ID_SCHEMA,
	deviceDisplayName: string().min(1),
	deviceDisplayNameSource: localRuntimeDisplayNameSourceSchema,
	platformFamily: localRuntimePlatformFamilySchema,
	osVersion: string().trim().min(1).max(120).nullable(),
	deviceType: localRuntimeDeviceTypeSchema,
	firstSeenAtMs: number().int().nonnegative(),
	lastObservedAtMs: number().int().nonnegative().nullable(),
	executionTargets: array(localRuntimeExecutionTargetSchema)
});
const localRuntimesConnectionIdentityAdoptionSchema = enumFactory(["safe_legacy_current"]);
const localRuntimesQuerySchema = strictObject({
	connectionIdentityAdoption: localRuntimesConnectionIdentityAdoptionSchema.optional(),
	includeUsageLimits: literal("true").optional()
});
const localRuntimesResponseSchema = strictObject({
	currentConnectedComputerId: START_BOUNDED_ID_SCHEMA.nullable().optional(),
	currentDeviceId: START_BOUNDED_ID_SCHEMA.nullable(),
	currentRuntimeLane: daemonServiceLaneSchema.nullable(),
	localRuntimes: array(localRuntimeComputerSchema),
	legacyDeviceEvidence: array(localRuntimeLegacyDeviceEvidenceSchema).optional()
});
const setLocalRuntimeDisplayNameBodySchema = strictObject({ displayName: string().trim().min(1).max(80).nullable() });
const setCurrentLocalAgentEnabledRouteParamsSchema = strictObject({ agentId: string().trim().min(1).max(256) });
const setCurrentLocalAgentEnabledBodySchema = strictObject({ enabled: boolean() });
const currentLocalAgentInstallationsRouteParamsSchema = strictObject({ agentId: string().trim().min(1).max(256) });
const selectCurrentLocalAgentInstallationBodySchema = strictObject({ installationId: string().trim().min(1).max(256) });
const setCurrentOpenMeldToolsMaintenanceBodySchema = strictObject({ automaticUpdatesEnabled: boolean() });
const setCurrentOpenMeldToolsMaintenanceResponseSchema = openMeldToolsMaintenanceMutationResultSchema;
const requestCurrentOpenMeldToolsUpdateResponseSchema = openMeldToolsUpdateRequestResultSchema;
const currentLocalAgentInstallationsResponseSchema = strictObject({
	agentId: string().trim().min(1).max(256),
	installations: array(localAgentInstallationOptionSchema),
	selectionStatus: enumFactory([
		"available",
		"blocked_by_work",
		"operation_in_progress"
	]),
	selectedInstallationId: string().trim().min(1).max(256).nullable()
});
const setCurrentAgentProfileWorkspaceRouteParamsSchema = strictObject({ agentProfileId: agentProfileIdSchema });
const setCurrentAgentProfileWorkspaceBodySchema = strictObject({
	workspaceMode: profileWorkspaceModeSchema,
	workspacePath: profileWorkingDirectorySchema.optional()
}).superRefine((value, context) => {
	if (value.workspaceMode === "custom" && !value.workspacePath) context.addIssue({
		code: "custom",
		message: "workspacePath is required for a custom profile workspace",
		path: ["workspacePath"]
	});
	if (value.workspaceMode === "openmeld_managed" && value.workspacePath) context.addIssue({
		code: "custom",
		message: "workspacePath must be omitted for an OpenMeld-managed profile workspace",
		path: ["workspacePath"]
	});
});
const setCurrentLocalAgentEnabledResponseSchema = strictObject({
	agentId: string().trim().min(1).max(256),
	changed: boolean(),
	enabled: boolean(),
	localRuntimes: localRuntimesResponseSchema,
	reportSyncStatus: enumFactory([
		"submitted",
		"skipped",
		"failed"
	]),
	status: enumFactory(["updated", "unchanged"])
});
const selectCurrentLocalAgentInstallationResponseSchema = currentLocalAgentInstallationsResponseSchema.extend({
	changed: boolean(),
	localRuntimes: localRuntimesResponseSchema,
	reportSyncStatus: enumFactory([
		"submitted",
		"skipped",
		"failed"
	]),
	status: enumFactory(["updated", "unchanged"])
});
const setCurrentAgentProfileWorkspaceResponseSchema = strictObject({
	agentProfileId: agentProfileIdSchema,
	status: literal("updated"),
	workspaceMode: profileWorkspaceModeSchema,
	workspacePath: string().min(1)
});
const refreshCurrentLocalAgentCatalogResponseSchema = strictObject({
	localRuntimes: localRuntimesResponseSchema,
	sync: openMeldDaemonSyncRouteCatalogPayloadSchema
});
const localRuntimeReplyReadinessStatusSchema = enumFactory([
	"ready",
	"blocked",
	"checking",
	"unknown"
]);
const localRuntimeReplyReadinessPrimaryBlockerSchema = enumFactory([
	"none",
	"current_runtime_missing",
	"agent_controller_report_not_ready",
	"agent_controller_report_stale",
	"agent_controller_report_unknown",
	"service_not_connected",
	"service_update_required",
	"service_unavailable"
]);
const localRuntimeReplyReadinessNextActionSchema = enumFactory([
	"none",
	"connect_service",
	"reconnect_service",
	"run_setup",
	"check_status",
	"update_service"
]);
const currentLocalRuntimeReplyReadinessResponseSchema = strictObject({
	canReplyFromThisComputer: boolean(),
	generatedAt: ISO_DATE_TIME_SCHEMA,
	message: string().trim().min(1).max(500),
	nextAction: localRuntimeReplyReadinessNextActionSchema,
	primaryBlocker: localRuntimeReplyReadinessPrimaryBlockerSchema,
	status: localRuntimeReplyReadinessStatusSchema,
	currentTarget: discriminatedUnion("availability", [strictObject({ availability: literal("missing") }), strictObject({
		availability: literal("resolved"),
		deviceId: START_BOUNDED_ID_SCHEMA,
		runtimeLane: daemonServiceLaneSchema
	})]),
	runtime: strictObject({
		deviceDisplayName: string().trim().min(1).nullable(),
		deviceId: START_BOUNDED_ID_SCHEMA.nullable(),
		executionTargetStatus: localRuntimeExecutionTargetStatusSchema.nullable(),
		lastObservedAtMs: number().int().nonnegative().nullable(),
		reasonCodes: array(reasonCodeSchema),
		signInOnlyBlockers: boolean().default(false),
		openMeldServiceRuntimeId: prepareReadinessRuntimeIdSchema.nullable(),
		runtimeLane: daemonServiceLaneSchema.nullable()
	}),
	service: strictObject({
		connected: boolean(),
		executionCompatibility: daemonExecutionCompatibilitySchema.nullable(),
		matchingCurrentRuntime: boolean(),
		reasonCodes: array(reasonCodeSchema),
		sessionDeviceId: START_BOUNDED_ID_SCHEMA.nullable(),
		sessionLane: daemonServiceLaneSchema.nullable()
	}),
	localComponents: localComponentUpdateProjectionSchema
});
//#endregion
export { currentLocalAgentInstallationsResponseSchema, currentLocalAgentInstallationsRouteParamsSchema, currentLocalRuntimeReplyReadinessResponseSchema, localRuntimeComputerFileAccessSchema, localRuntimeComputerSchema, localRuntimeConnectionStatusSchema, localRuntimeDeviceTypeSchema, localRuntimeDisplayNameSourceSchema, localRuntimeExecutionTargetCapabilitySchema, localRuntimeExecutionTargetSchema, localRuntimeExecutionTargetStatusSchema, localRuntimeLegacyDeviceEvidenceSchema, localRuntimeLocalAgentControlSchema, localRuntimeLocalFolderPickerSchema, localRuntimeLocalProjectControlSchema, localRuntimePlatformFamilySchema, localRuntimeReplyReadinessNextActionSchema, localRuntimeReplyReadinessPrimaryBlockerSchema, localRuntimeReplyReadinessStatusSchema, localRuntimesConnectionIdentityAdoptionSchema, localRuntimesQuerySchema, localRuntimesResponseSchema, refreshCurrentLocalAgentCatalogResponseSchema, requestCurrentOpenMeldToolsUpdateResponseSchema, selectCurrentLocalAgentInstallationBodySchema, selectCurrentLocalAgentInstallationResponseSchema, setCurrentAgentProfileWorkspaceBodySchema, setCurrentAgentProfileWorkspaceResponseSchema, setCurrentAgentProfileWorkspaceRouteParamsSchema, setCurrentLocalAgentEnabledBodySchema, setCurrentLocalAgentEnabledResponseSchema, setCurrentLocalAgentEnabledRouteParamsSchema, setCurrentOpenMeldToolsMaintenanceBodySchema, setCurrentOpenMeldToolsMaintenanceResponseSchema, setLocalRuntimeDisplayNameBodySchema };

//# sourceMappingURL=local-runtimes.js.map